import React, { useEffect, useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getCurrentEvents, getUpcomingEvents } from '../api/events.api';
import { Event } from '../types';
import LoadingSpinner from '../components/LoadingSpinner';

const EventsPage: React.FC = () => {
  const [currentEvents, setCurrentEvents] = useState<Event[]>([]);
  const [upcomingEvents, setUpcomingEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const categoryFilter = searchParams.get('category');

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const [currentResponse, upcomingResponse] = await Promise.all([
          getCurrentEvents(50),
          getUpcomingEvents(50),
        ]);
        
        let currentEventsData = Array.isArray(currentResponse.data) ? currentResponse.data : [];
        let upcomingEventsData = Array.isArray(upcomingResponse.data) ? upcomingResponse.data : [];
        
        // Categorize events based on status
        // Current events should include: "Ongoing", "Registration Open" (if dates match)
        // Upcoming events should include: "Coming Soon", "Registration Open" (if future dates)
        // Completed/Cancelled events should be filtered out or handled separately
        
        // Filter out "Coming Soon" events from current events
        const comingSoonFromCurrent = currentEventsData.filter(
          (event) => {
            const status = (event.status || '').toLowerCase();
            return status === 'coming soon' || status === 'coming_soon';
          }
        );
        const actualCurrentEvents = currentEventsData.filter(
          (event) => {
            const status = (event.status || '').toLowerCase();
            return status !== 'coming soon' && status !== 'coming_soon';
          }
        );
        
        // Move "Ongoing" events from upcoming to current (they should always be current)
        const ongoingInUpcoming = upcomingEventsData.filter(
          (event) => {
            const status = (event.status || '').toLowerCase();
            return status === 'ongoing';
          }
        );
        const actualUpcomingEvents = upcomingEventsData.filter(
          (event) => {
            const status = (event.status || '').toLowerCase();
            return status !== 'ongoing';
          }
        );
        
        // Create a Set of event IDs to avoid duplicates
        const currentEventIds = new Set(actualCurrentEvents.map(e => e.id));
        const upcomingEventIds = new Set(actualUpcomingEvents.map(e => e.id));
        
        // Add "Ongoing" events from upcoming to current (avoiding duplicates)
        const newOngoingEvents = ongoingInUpcoming.filter(e => !currentEventIds.has(e.id));
        const finalCurrentEvents = [...actualCurrentEvents, ...newOngoingEvents];
        
        // Add "Coming Soon" events from current to upcoming (avoiding duplicates)
        const newComingSoonEvents = comingSoonFromCurrent.filter(e => !upcomingEventIds.has(e.id));
        const finalUpcomingEvents = [...actualUpcomingEvents, ...newComingSoonEvents];
        
        setUpcomingEvents(finalUpcomingEvents);
        setCurrentEvents(finalCurrentEvents);
      } catch (err) {
        console.error('Error fetching events:', err);
        setCurrentEvents([]);
        setUpcomingEvents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  // Filter events by category (if category filter is applied)
  const filteredCurrentEvents = useMemo(() => {
    if (!categoryFilter) {
      return currentEvents;
    }
    const decodedCategory = decodeURIComponent(categoryFilter).toLowerCase();
    return currentEvents.filter((event) =>
      event.name.toLowerCase().includes(decodedCategory) ||
      (event.description && event.description.toLowerCase().includes(decodedCategory)) ||
      (event.location && event.location.toLowerCase().includes(decodedCategory))
    );
  }, [currentEvents, categoryFilter]);

  const filteredUpcomingEvents = useMemo(() => {
    if (!categoryFilter) {
      return upcomingEvents;
    }
    const decodedCategory = decodeURIComponent(categoryFilter).toLowerCase();
    return upcomingEvents.filter((event) =>
      event.name.toLowerCase().includes(decodedCategory) ||
      (event.description && event.description.toLowerCase().includes(decodedCategory)) ||
      (event.location && event.location.toLowerCase().includes(decodedCategory))
    );
  }, [upcomingEvents, categoryFilter]);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="container py-5">
      {categoryFilter && (
        <div className="mb-3">
          <Link to="/events" className="btn btn-outline-secondary btn-sm mb-2">
            <i className="bi bi-arrow-left me-1"></i>
            View All Events
          </Link>
          <div className="alert alert-info mb-0">
            <i className="bi bi-funnel me-2"></i>
            Showing events for: <strong>{decodeURIComponent(categoryFilter)}</strong>
          </div>
        </div>
      )}
      <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between mb-4 gap-3">
        <div>
          <h1 className="h3 section-title">All Events</h1>
          <p className="text-muted-soft mb-0">
            {categoryFilter 
              ? `Events in ${decodeURIComponent(categoryFilter)}`
              : 'Find the next arena to conquer.'}
          </p>
        </div>
        <Link to="/auth" className="btn btn-arena w-30 w-md-auto">
          Join the League
        </Link>
      </div>

      {/* Current Events Section */}
      <section className="mb-5">
        <div className="d-flex align-items-center gap-2 mb-4">
          <h2 className="h4 section-title mb-0">Current Events</h2>
          <span className="badge bg-success badge-status">{filteredCurrentEvents.length}</span>
        </div>
        {filteredCurrentEvents.length === 0 ? (
          <div className="alert alert-secondary mb-0">
            {categoryFilter 
              ? `No current events found for "${decodeURIComponent(categoryFilter)}".`
              : 'No current events available.'}
          </div>
        ) : (
          <div className="row g-4">
            {filteredCurrentEvents.map((event) => (
                  <div className="col-md-6 col-lg-4" key={event.id}>
                    <div className="card card-surface h-100">
                      {event.bannerUrl ? (
                        <img 
                          src={event.bannerUrl} 
                          className="card-img-top" 
                          alt={event.name}
                          style={{ 
                            width: '100%', 
                            height: '200px', 
                            objectFit: 'cover',
                            display: 'block'
                          }}
                          onError={(e) => {
                            // Show placeholder if image fails to load
                            e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="200"%3E%3Crect fill="%230a0a0a" width="400" height="200"/%3E%3Ctext fill="%23ffffff" font-family="Arial" font-size="18" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EEvent Image%3C/text%3E%3C/svg%3E';
                          }}
                        />
                      ) : (
                        <div 
                          className="card-img-top bg-dark d-flex align-items-center justify-content-center text-white"
                          style={{ height: '200px', backgroundColor: '#0a0a0a' }}
                        >
                          <i className="bi bi-calendar-event" style={{ fontSize: '3rem', opacity: 0.5 }}></i>
                        </div>
                      )}
                      <div className="card-body d-flex flex-column">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="badge bg-success badge-status">
                            {event.status || 'Ongoing'}
                          </span>
                          <span className="text-muted-soft small">
                            {event.startDate ? new Date(event.startDate).toLocaleDateString() : 'TBA'}
                          </span>
                        </div>
                        <h5 className="mb-2" style={{ fontSize: 'clamp(1rem, 2.5vw, 1.25rem)' }}>{event.name}</h5>
                        <p className="text-muted-soft flex-grow-1 small" style={{ fontSize: 'clamp(0.875rem, 2vw, 1rem)' }}>
                          {event.description || 'High-intensity matches with verified officials.'}
                        </p>
                        <Link to={`/events/${event.id}`} className="btn btn-outline-secondary mt-auto">
                          Details
                        </Link>
                      </div>
                    </div>
                  </div>
            ))}
          </div>
        )}
      </section>

      {/* Upcoming Events Section */}
      <section className="mb-4">
        <div className="d-flex align-items-center gap-2 mb-4">
          <h2 className="h4 section-title mb-0">Upcoming Events</h2>
          <span className="badge bg-info badge-status">{filteredUpcomingEvents.length}</span>
        </div>
        {filteredUpcomingEvents.length === 0 ? (
          <div className="alert alert-secondary mb-0">
            {categoryFilter 
              ? `No upcoming events found for "${decodeURIComponent(categoryFilter)}".`
              : 'No upcoming events available.'}
          </div>
      ) : (
        <div className="row g-4">
            {filteredUpcomingEvents.map((event) => (
            <div className="col-md-6 col-lg-4" key={event.id}>
              <div className="card card-surface h-100">
                      {event.bannerUrl ? (
                        <img 
                          src={event.bannerUrl} 
                          className="card-img-top" 
                          alt={event.name}
                          style={{ 
                            width: '100%', 
                            height: '200px', 
                            objectFit: 'cover',
                            display: 'block'
                          }}
                          onError={(e) => {
                            // Show placeholder if image fails to load
                            e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="200"%3E%3Crect fill="%230a0a0a" width="400" height="200"/%3E%3Ctext fill="%23ffffff" font-family="Arial" font-size="18" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EEvent Image%3C/text%3E%3C/svg%3E';
                          }}
                        />
                      ) : (
                        <div 
                          className="card-img-top bg-dark d-flex align-items-center justify-content-center text-white"
                          style={{ height: '200px', backgroundColor: '#0a0a0a' }}
                        >
                          <i className="bi bi-calendar-event" style={{ fontSize: '3rem', opacity: 0.5 }}></i>
                        </div>
                )}
                <div className="card-body d-flex flex-column">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="badge bg-info badge-status">
                            {event.status || 'Coming Soon'}
                    </span>
                    <span className="text-muted-soft small">
                      {event.startDate ? new Date(event.startDate).toLocaleDateString() : 'TBA'}
                    </span>
                  </div>
                        <h5 className="mb-2" style={{ fontSize: 'clamp(1rem, 2.5vw, 1.25rem)' }}>{event.name}</h5>
                        <p className="text-muted-soft flex-grow-1 small" style={{ fontSize: 'clamp(0.875rem, 2vw, 1rem)' }}>
                    {event.description || 'High-intensity matches with verified officials.'}
                  </p>
                  <Link to={`/events/${event.id}`} className="btn btn-outline-secondary mt-auto">
                    Details
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      </section>
    </div>
  );
};

export default EventsPage;
